import { makeObservable, observable, action, runInAction, computed } from 'mobx';

import { inject, injectable } from '@servicetitan/react-ioc';

import { LoadStatus } from '../../../common/enums/load-status';
import { TelecomApi, TelecomBrandData } from '../api/telecom.api';
import { ManageTenantStore } from '../../stores/manage-tenant.store';
import { NotificationsStore } from '../../../common/stores/notifications.store';

@injectable()
export class TcrStore {
    @observable brands: TelecomBrandData[] = [];
    @observable loadStatus: LoadStatus = LoadStatus.None;
    @observable error: string | null = null;
    @observable initialised = false;

    @computed get tenant() {
        return this.manageTenantStore.tenant;
    }

    constructor(
        @inject(TelecomApi) private telecomApi: TelecomApi,
        @inject(ManageTenantStore) private manageTenantStore: ManageTenantStore,
        @inject(NotificationsStore) private notificationsStore: NotificationsStore,
    ) {
        makeObservable(this);
    }

    @action init = (): void => {
        if (!this.initialised) {
            this.initialised = true;

            if (!this.manageTenantStore.tenant) {
                this.error = 'No tenant selected';
                return;
            }

            this.fetchBrands();
        }
    };

    @action fetchBrands = async (): Promise<void> => {
        if (!this.tenant!.internalId || !this.tenant!.partnerId) {
            this.error = 'Missing tenant or partner ID';
            return;
        }

        try {
            this.loadStatus = LoadStatus.Loading;
            this.error = null;

            const response = await this.telecomApi.brands(
                this.tenant!.internalId,
                this.tenant!.partnerId,
            );

            runInAction(() => {
                this.brands = response.data;
                this.loadStatus = LoadStatus.Ok;
            });
        } catch (error) {
            runInAction(() => {
                this.loadStatus = LoadStatus.Error;
                this.error = error instanceof Error ? error.message : 'Failed to load brands';
                this.brands = [];
            });
        }
    };

    @action syncBrand = async (brandId: number, ownerId: number): Promise<void> => {
        if (!this.tenant?.internalId || !this.tenant?.partnerId) {
            this.notificationsStore.notifyInSnackbar({ title: 'Missing tenant or partner ID' });
            return;
        }

        try {
            await this.telecomApi.ownersGET(
                brandId,
                ownerId,
                this.tenant.internalId,
                this.tenant.partnerId,
            );

            this.notificationsStore.notifyInSnackbar({ title: 'Brand synced successfully' });

            // Refresh the brand data
            await this.fetchBrands();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to sync brand';
            this.notificationsStore.notifyInSnackbar({ title: message });
        }
    };

    @action deleteBrand = async (brandId: number, ownerId: number): Promise<void> => {
        if (!this.tenant?.internalId || !this.tenant.partnerId) {
            this.notificationsStore.notifyInSnackbar({ title: 'Missing tenant or partner ID' });
            return;
        }

        try {
            await this.telecomApi.ownersDELETE(
                brandId,
                ownerId,
                this.tenant.internalId,
                this.tenant.partnerId,
            );

            this.notificationsStore.notifyInSnackbar({ title: 'Brand deleted successfully' });

            // Remove from local state
            runInAction(() => {
                this.brands = this.brands.filter(brand => brand.id !== brandId);
            });
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Failed to delete brand';
            this.notificationsStore.notifyInSnackbar({ title: message });
        }
    };

    @action refresh = (): void => {
        this.fetchBrands();
    };
}
